import { parseColorHexRGB } from '@microsoft/fast-colors';
import { Direction } from "@microsoft/fast-web-utilities";
import { SwatchRGB, Palette, PaletteRGB, Swatch, InteractiveSwatchSet, ContrastTarget, calculateNeutralLayer, calculateFocusStrokeOuter, calculateFocusStrokeInner, calculateForegroundOnColor, calculateDeltaSwatch } from './color';

// TODO: generate design tokens at build time as preparation for design API?
import designSystemDefaults from './design-tokens/default.tokens.json';
import { NatGenDesignToken } from './design-tokens/design-token';
import { DesignToken } from './foundation/design-token';
import { FASTElement } from '@microsoft/fast-element';
import { DesignTokenResolver } from './foundation/design-token-node';

const { create } = DesignToken;

export interface Recipe<T> {
    evaluate(resolver: DesignTokenResolver, reference?: Swatch): T;
}

export let designTokens: Record<string, NatGenDesignToken<any>> = {};

export function getToken(name: string) {
    if (designTokens.hasOwnProperty(name))
        return designTokens[name].token;
    else
        console.warn(`Token ${name} not found`);
}

/**
 * Get a token styles for CSS bindings.
 */
export function getBindingToken(name: string) {
    getToken(name).createCSS();
}

export function createDesignTokensFromJson(jsonRoot) {
    let aliases: { path: string, target: string }[] = [];

    (function getTokens(root, path = '', type = '') {
        type = root.$type || type;

        if ('$value' in root) {
            if (root.$value.toString().includes('{'))
                aliases.push({ path, target: root.$value });
            else {
                switch (type) {
                    case "number":
                        designTokens[path] = new NatGenDesignToken<number>({
                            defaultValue: root.$value,
                            name: path
                        });
                        break;

                    case "color":
                        const color = parseColorHexRGB(root.$value);
                        designTokens[path] = new NatGenDesignToken<SwatchRGB>({
                            defaultValue: new SwatchRGB(color.r, color.g, color.b),
                            name: path
                        });
                        break;

                    default:
                        designTokens[path] = new NatGenDesignToken<string>({
                            defaultValue: root.$value,
                            name: path
                        });
                }

                designTokens[path].create();
            }
        } else {
            Object.keys(root).forEach(key => {
                if(!key.startsWith('$'))
                    getTokens(root[key], path.length > 0 ? `${path}-${key}` : key, type)
            });
        }
    })(jsonRoot);

    while (aliases.length > 0) {
        const alias = aliases.shift();

        if (aliases.findIndex(a => a.path === alias.target) > -1)
        {
            aliases.push(alias);
            continue;
        }

        const targetPath = alias.target.replace(/[\{\}]/g, '').replaceAll('.', '-');

        if (designTokens.hasOwnProperty(targetPath)) {
            designTokens[alias.path] = new NatGenDesignToken<any>({
                name: alias.path
            });

            const targetToken = designTokens[targetPath];
            designTokens[alias.path].setAlias(targetToken.token);
        }
        else
            console.warn(`Token ${targetPath} not found for alias ${alias.path}`, designTokens);
    }
}

// TODO: Get rid of this and pass element to create?
export function overrideDesignTokensFromJson(jsonRoot, element: FASTElement) {
    let aliases: { path: string, target: string }[] = [];

    (function getTokens(root, path = '', type = '') {
        type = root.$type || type;

        if ('$value' in root) {
            if (root.$value.toString().includes('{'))
                aliases.push({ path, target: root.$value });
            else {
                switch (type) {
                    case "number":
                        if (designTokens[path] === undefined) {
                            designTokens[path] = new NatGenDesignToken<number>({
                                defaultValue: root.$value,
                                name: path
                            });

                            designTokens[path].create();
                        }

                        designTokens[path].setValueFor(element, root.$value);
                        break;
                    case "color":
                        const color = parseColorHexRGB(root.$value);

                        if (designTokens[path] === undefined) {
                            designTokens[path] = new NatGenDesignToken<SwatchRGB>({
                                defaultValue: new SwatchRGB(color.r, color.g, color.b),
                                name: path
                            });

                            designTokens[path].create();
                        }

                        designTokens[path].setValueFor(element, new SwatchRGB(color.r, color.g, color.b));
                        break;

                    default:
                        if (designTokens[path] === undefined) {
                            designTokens[path] = new NatGenDesignToken<string>({
                                defaultValue: root.$value,
                                name: path
                            });

                            designTokens[path].create();
                        }

                        designTokens[path].setValueFor(element, root.$value);
                }
            }
        } else {
            Object.keys(root).forEach(key => {
                if(!key.startsWith('$'))
                    getTokens(root[key], path.length > 0 ? `${path}-${key}` : key, type)
            });
        }
    })(jsonRoot);

    while (aliases.length > 0) {
        const alias = aliases.shift();

        if (aliases.findIndex(a => a.path === alias.target) > -1)
        {
            aliases.push(alias);
            continue;
        }

        const targetPath = alias.target.replace(/[\{\}]/g, '').replaceAll('.', '-');

        if (designTokens.hasOwnProperty(targetPath)) {
            const targetToken = designTokens[targetPath];
            designTokens[alias.path].setValueFor(element, targetToken.token);
        }
        else
            console.warn(`Token ${targetPath} not found for alias ${alias.path}`, designTokens);
    }
}

createDesignTokensFromJson(designSystemDefaults);

export const brand = create<string>({
    name: 'brand',
    cssCustomPropertyName: null
}).withDefault('ngic');

// Color

// TODO: Remove neutral palette and use math to calculate dark mode colors? Currently neutral button background does not work in dark mode.
export const neutralPalette = create<Palette>({
    name: 'neutral-palette',
    cssCustomPropertyName: null
}).withDefault(resolve => PaletteRGB.withComponentStateColorPalette(resolve(designTokens['color-neutral-base'].token) as SwatchRGB));

// Color Recipes

export const neutralLayer1Recipe = create<Recipe<Swatch>>({
    name: 'neutral-layer-1-recipe',
    cssCustomPropertyName: null
}).withDefault({
    evaluate: (resolve: DesignTokenResolver): Swatch =>
        calculateNeutralLayer(
            resolve(neutralPalette),
            resolve(designTokens['color-base-layer-luminance'].token)
        )
});

export const neutralLayer2Recipe = create<Recipe<Swatch>>({
    name: 'neutral-layer-2-recipe',
    cssCustomPropertyName: null
}).withDefault({
    evaluate: (resolve: DesignTokenResolver): Swatch =>
        calculateNeutralLayer(
            resolve(neutralPalette),
            resolve(designTokens['color-base-layer-luminance'].token),
            resolve(designTokens['color-recipe-neutral-layer-fill-rest'].token)
        )
});

export const neutralLayer3Recipe = create<Recipe<Swatch>>({
    name: 'neutral-layer-3-recipe',
    cssCustomPropertyName: null
}).withDefault({
    evaluate: (resolve: DesignTokenResolver): Swatch =>
        calculateNeutralLayer(
            resolve(neutralPalette),
            resolve(designTokens['color-base-layer-luminance'].token),
            resolve(designTokens['color-recipe-neutral-layer-fill-rest'].token) * 2
        )
});

export const neutralLayer4Recipe = create<Recipe<Swatch>>({
    name: 'neutral-layer-4-recipe',
    cssCustomPropertyName: null
}).withDefault({
    evaluate: (resolve: DesignTokenResolver): Swatch =>
        calculateNeutralLayer(
            resolve(neutralPalette),
            resolve(designTokens['color-base-layer-luminance'].token),
            resolve(designTokens['color-recipe-neutral-layer-fill-rest'].token) * 3
        )
});

export const neutralForegroundRecipe = create<Recipe<InteractiveSwatchSet>>({
    name: 'neutral-foreground-recipe',
    cssCustomPropertyName: null
}).withDefault({
    evaluate: (resolve: DesignTokenResolver): InteractiveSwatchSet =>
        InteractiveSwatchSet.createFromContrast(
            resolve(neutralPalette),
            resolve(fillColor),
            ContrastTarget.Text, // TODO: Should this be a token?
            resolve(designTokens['color-recipe-neutral-foreground-rest'].token),
            resolve(designTokens['color-recipe-neutral-foreground-hover'].token),
            resolve(designTokens['color-recipe-neutral-foreground-active'].token),
            resolve(designTokens['color-recipe-neutral-foreground-focus'].token)
        )
});

export const neutralInputFillRecipe = create<Recipe<InteractiveSwatchSet>>({
    name: 'neutral-input-fill-recipe',
    cssCustomPropertyName: null
}).withDefault({
    evaluate: (resolve: DesignTokenResolver, reference?: Swatch): InteractiveSwatchSet =>
        InteractiveSwatchSet.create(
            resolve(neutralPalette),
            reference || resolve(fillColor),
            resolve(designTokens['color-recipe-neutral-input-fill-rest'].token),
            resolve(designTokens['color-recipe-neutral-input-fill-hover'].token),
            resolve(designTokens['color-recipe-neutral-input-fill-active'].token),
            resolve(designTokens['color-recipe-neutral-input-fill-focus'].token)
        )
});

export const neutralStealthFillRecipe = create<Recipe<InteractiveSwatchSet>>({
    name: 'neutral-stealth-fill-recipe',
    cssCustomPropertyName: null
}).withDefault({
    evaluate: (resolve: DesignTokenResolver, reference?: Swatch): InteractiveSwatchSet =>
        InteractiveSwatchSet.create(
            resolve(neutralPalette),
            reference || resolve(fillColor),
            resolve(designTokens['color-recipe-neutral-stealth-fill-rest'].token),
            resolve(designTokens['color-recipe-neutral-stealth-fill-hover'].token),
            resolve(designTokens['color-recipe-neutral-stealth-fill-active'].token),
            resolve(designTokens['color-recipe-neutral-stealth-fill-focus'].token)
        )
});

export const neutralStrokeRecipe = create<Recipe<InteractiveSwatchSet>>({
    name: "neutral-stroke-recipe",
    cssCustomPropertyName: null,
}).withDefault({
    evaluate: (resolve: DesignTokenResolver): InteractiveSwatchSet =>
        InteractiveSwatchSet.create(
            resolve(neutralPalette),
            resolve(fillColor),
            resolve(designTokens['color-recipe-neutral-stroke-rest'].token),
            resolve(designTokens['color-recipe-neutral-stroke-hover'].token),
            resolve(designTokens['color-recipe-neutral-stroke-active'].token),
            resolve(designTokens['color-recipe-neutral-stroke-focus'].token)
        ),
});

export const neutralDividerRecipe = create<Recipe<Swatch>>({
    name: 'neutral-divider',
    cssCustomPropertyName: null
}).withDefault({
    evaluate: (resolve: DesignTokenResolver, reference?: Swatch): Swatch =>
        calculateDeltaSwatch(
            resolve(neutralPalette),
            reference || resolve(fillColor),
            resolve(designTokens['color-recipe-neutral-divider-rest'].token)
        )
});

export const focusStrokeOuterRecipe = create<Recipe<Swatch>>({
    name: "focus-stroke-outer-recipe",
    cssCustomPropertyName: null,
}).withDefault({
    evaluate: (resolve: DesignTokenResolver): Swatch =>
        calculateFocusStrokeOuter(
            resolve(neutralPalette),
            resolve(fillColor)
        ),
});

export const focusStrokeInnerRecipe = create<Recipe<Swatch>>({
    name: "focus-stroke-inner-recipe",
    cssCustomPropertyName: null,
}).withDefault({
    evaluate: (resolve: DesignTokenResolver): Swatch =>
        calculateFocusStrokeInner(
            resolve(neutralPalette),
            resolve(fillColor),
            resolve(focusStrokeOuterRecipe).evaluate(resolve)
        ),
});

// Recipe Colors

export const neutralLayer1 = create<Swatch>('neutral-layer-1').withDefault(resolve =>
    resolve(neutralLayer1Recipe).evaluate(resolve)
);

export const neutralLayer2 = create<Swatch>('neutral-layer-2').withDefault(resolve =>
    resolve(neutralLayer2Recipe).evaluate(resolve)
);

export const neutralLayer3 = create<Swatch>('neutral-layer-3').withDefault(resolve =>
    resolve(neutralLayer3Recipe).evaluate(resolve)
);

export const neutralLayer4 = create<Swatch>('neutral-layer-4').withDefault(resolve =>
    resolve(neutralLayer4Recipe).evaluate(resolve)
);

export const fillColor = create<Swatch>('fill-color').withDefault(resolve =>
    resolve(neutralLayer1)
);

export const neutralForegroundRest = create<Swatch>('neutral-foreground-rest').withDefault(resolve =>
    resolve(neutralForegroundRecipe).evaluate(resolve).rest
);

export const neutralForegroundHover = create<Swatch>('neutral-foreground-hover').withDefault(resolve =>
    resolve(neutralForegroundRecipe).evaluate(resolve).hover
);

export const neutralForegroundActive = create<Swatch>('neutral-foreground-active').withDefault(resolve =>
    resolve(neutralForegroundRecipe).evaluate(resolve).active
);

export const neutralForegroundFocus = create<Swatch>('neutral-foreground-focus').withDefault(resolve =>
    resolve(neutralForegroundRecipe).evaluate(resolve).focus
);

export const neutralInputFillRest = create<Swatch>('neutral-input-fill-rest').withDefault(resolve =>
    resolve(neutralInputFillRecipe).evaluate(resolve).rest
);

export const neutralInputFillHover = create<Swatch>('neutral-input-fill-hover').withDefault(resolve =>
    resolve(neutralInputFillRecipe).evaluate(resolve).hover
);

export const neutralInputFillActive = create<Swatch>('neutral-input-fill-active').withDefault(resolve =>
    resolve(neutralInputFillRecipe).evaluate(resolve).active
);

export const neutralInputFillFocus = create<Swatch>('neutral-input-fill-focus').withDefault(resolve =>
    resolve(neutralInputFillRecipe).evaluate(resolve).focus
);

// TODO: Recipe to base on current layer?
export const neutralStealthFillRest = create<string>('neutral-stealth-fill-rest').withDefault('transparent');

export const neutralStealthFillHover = create<Swatch>('neutral-stealth-fill-hover').withDefault(resolve =>
    resolve(neutralStealthFillRecipe).evaluate(resolve).hover
);

export const neutralStealthFillActive = create<Swatch>('neutral-stealth-fill-active').withDefault(resolve =>
    resolve(neutralStealthFillRecipe).evaluate(resolve).active
);

export const neutralStealthFillFocus = create<Swatch>('neutral-stealth-fill-focus').withDefault(resolve =>
    resolve(neutralStealthFillRecipe).evaluate(resolve).focus
);

export const neutralStrokeRest = create<Swatch>('neutral-stroke-rest').withDefault(resolve =>
    resolve(neutralStrokeRecipe).evaluate(resolve).rest
);

export const neutralStrokeHover = create<Swatch>('neutral-stroke-hover').withDefault(resolve =>
    resolve(neutralStrokeRecipe).evaluate(resolve).hover
);

export const neutralStrokeActive = create<Swatch>('neutral-stroke-active').withDefault(resolve =>
    resolve(neutralStrokeRecipe).evaluate(resolve).active
);

export const neutralStrokeFocus = create<Swatch>('neutral-stroke-focus').withDefault(resolve =>
    resolve(neutralStrokeRecipe).evaluate(resolve).focus
);

export const neutralDivider = create<Swatch>('neutral-divider').withDefault(resolve =>
    resolve(neutralDividerRecipe).evaluate(resolve)
);

export const focusStrokeOuter = create<Swatch>('focus-stroke-outer').withDefault(resolve =>
    resolve(focusStrokeOuterRecipe).evaluate(resolve)
);

export const focusStrokeInner = create<Swatch>('focus-stroke-inner').withDefault(resolve =>
    resolve(focusStrokeInnerRecipe).evaluate(resolve)
);

export const foregroundOnPrimaryRest = create<Swatch>('foreground-on-primary-rest').withDefault(resolve =>
    calculateForegroundOnColor(resolve(designTokens['color-primary-base'].token), ContrastTarget.Normal)
);

export const foregroundOnPrimaryHover = create<Swatch>('foreground-on-primary-hover').withDefault(resolve =>
    calculateForegroundOnColor(resolve(designTokens['color-primary-hover'].token), ContrastTarget.Normal)
);

export const foregroundOnPrimaryActive = create<Swatch>('foreground-on-primary-active').withDefault(resolve =>
    calculateForegroundOnColor(resolve(designTokens['color-primary-active'].token), ContrastTarget.Normal)
);

export const foregroundOnPrimaryFocus = create<Swatch>('foreground-on-primary-focus').withDefault(resolve =>
    calculateForegroundOnColor(resolve(designTokens['color-primary-focus'].token), ContrastTarget.Normal)
);

export const foregroundOnSecondaryRest = create<Swatch>('foreground-on-secondary-rest').withDefault(resolve =>
    calculateForegroundOnColor(resolve(designTokens['color-secondary-base'].token), ContrastTarget.Normal)
);

export const foregroundOnSecondaryHover = create<Swatch>('foreground-on-secondary-hover').withDefault(resolve =>
    calculateForegroundOnColor(resolve(designTokens['color-secondary-hover'].token), ContrastTarget.Normal)
);

export const foregroundOnSecondaryActive = create<Swatch>('foreground-on-secondary-active').withDefault(resolve =>
    calculateForegroundOnColor(resolve(designTokens['color-secondary-active'].token), ContrastTarget.Normal)
);

export const foregroundOnSecondaryFocus = create<Swatch>('foreground-on-secondary-focus').withDefault(resolve =>
    calculateForegroundOnColor(resolve(designTokens['color-secondary-focus'].token), ContrastTarget.Normal)
);

// Typography

export const direction = create<Direction>("direction").withDefault(Direction.ltr);
